/**
 * IdleKeypad.js
 * Talkroute
 *
 * Created by Vel Genov on 1/29/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

// @ts-ignore
import ContactDialogWithDropdown from "@components/Contacts/ContactDialogWithDropdown";
import { addLeadingCountryCode, formatPhoneNumber, trimPhoneNumberPlusSign } from "@helpers/functions/phoneNumberFormatter";
import { IconButton } from "@material-ui/core";
import CrossIcon from "@resources/icons/cross-small.svg";
import AddContactIcon from "@resources/icons/ic-add-contact.svg";
import { validatePhoneNumber } from "@services/dataClasses/CallData";
import { push } from "connected-react-router";
import queryString from "query-string";
import React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { Confirm, Divider, Grid } from "semantic-ui-react";
import { makeCall, setLastCalledNumber, setNumberToCall } from "../../actions/callActions";
import { openMicUnavailableModal } from "../../actions/micActions";
import keyConstants from "../../constants/keyConstants";
import limitConstants from "../../constants/limitConstants";
import { domIds } from "../../constants/testConstants";
import uiString from "../../constants/uiString";
import KeyComponent, { Types } from "./KeyComponent";

interface IIdleKeypadProps {
  numberToCall: string;
  outgoingPhoneNumber: string;
  defaultOutgoingPhoneNumber: string;
  lastCallOutgoingPhoneNumber: string;
  lastCalledNumber: string;
  filterBy: string;
  search: string;
  isPreferencesVisible: boolean;
  match?: any;
  micIsBlocked: boolean;
  micCheckInProgress: boolean;
  setNumberToCall: (number: string | null) => void;
  // redux actions
  makeCall: (phoneNumber: string, outgoingPhoneNumber?: string) => {};
  setLastCalledNumber: (phoneNumber: string, outgoingPhoneNumber: string) => {};
  push: (url: string) => any;
  openMicUnavailableModal: () => any;
}

interface IIdleKeypadState {
  textFieldValue: string;
  invalidNumberMessageOpen: boolean;
  keyDownNumber: number;
  hideLastCalledNumber: boolean;
  isOpen: boolean;
}

/**
 * IdleKeypad component
 */
class IdleKeypad extends React.Component<IIdleKeypadProps, IIdleKeypadState> {
  public inputField;

  public static defaultProps = {
    lastCalledNumber: ``,
  };

  /**
   * Add a string value to the textFieldValue state parameter
   *
   * @param valueToAdd a string value to add to the textFieldValue state parameter
   */
  addToTextFieldValue = (valueToAdd) => {
    const { textFieldValue } = this.state;
    const updatedValue = `${textFieldValue}${valueToAdd}`;

    this.setTextFieldValue(updatedValue);
  };

  /**
   * Set the textFieldValue's value.
   *
   * @param value the value to set textFieldValue to
   */
  setTextFieldValue = (value, callback?) => {
    if (value?.length <= limitConstants.MAX_NUMBER_DIGITS) {
      this.setState({ textFieldValue: value }, callback);
    }
  };

  /**
   * Clears the phone number text field's value
   */
  clearTextFieldValue = () => {
    this.setState({ textFieldValue: "" });
  };

  /**
   * Called when a numeric key on the keypad is pressed
   *
   * @param number the number pressed
   */
  onNumPadClick = (number) => this.addToTextFieldValue(number);

  /**
   * Called when the call button is clicked. Verifies the current number is valid and calls it if so
   * If the number is invalid, it shows a message to the user
   */
  onCallButtonClick = () => {
    const {
      defaultOutgoingPhoneNumber,
      lastCalledNumber,
      lastCallOutgoingPhoneNumber,
      micIsBlocked,
      micCheckInProgress,
      openMicUnavailableModal
    } = this.props;
    const { textFieldValue, hideLastCalledNumber } = this.state;

    const outgoingPhoneNumber = defaultOutgoingPhoneNumber

    if (outgoingPhoneNumber) {
      if (!textFieldValue.length && hideLastCalledNumber) {
        this.setState({
          hideLastCalledNumber: false,
        });
        return;
      }
      const numberToCall = textFieldValue.length
        ? textFieldValue
        : lastCalledNumber;

      if (micIsBlocked && !micCheckInProgress) {
        openMicUnavailableModal();
      } else if (!micCheckInProgress) {
        if (!!validatePhoneNumber(numberToCall)) {
          this.sendCallAction(numberToCall, outgoingPhoneNumber);
          this.clearTextFieldValue();
        } else {
          this.openInvalidPhoneNumberMessage();
        }
      }
    }
  };

  /**
   * Send an action to initiate a call
   *
   * @param numberToCall the phone number to call
   */
  sendCallAction = (numberToCall: string, outgoingPhoneNumber: string) => {
    const {
      makeCall: makeCallLocal,
      setLastCalledNumber: setLastCalledNumberLocal,
    } = this.props;

    makeCallLocal(numberToCall, outgoingPhoneNumber);
    setLastCalledNumberLocal(numberToCall, outgoingPhoneNumber);
  };

  /**
   * Called when the user changes the value of the phone number text field
   *
   * @param value the current value of the text field
   */
  onTextFieldChanged = (value) => {
    const numericValue = value?.replace(/\D/g, "").trim();
    this.setTextFieldValue(numericValue);
  };

  /**
   * Opens the invalid phone number message and displays it to the user
   */
  openInvalidPhoneNumberMessage = () => {
    this.setState({ invalidNumberMessageOpen: true });
  };

  /**
   * Closes the invalid phone number message
   */
  closeInvalidPhoneNumberMessage = () => {
    this.setState({ invalidNumberMessageOpen: false });
  };

  /**
   * Adds the invalid phone number message to the view stack
   * The message is either added in an open or closed state based on the invalidPhoneNumberMessage open
   * state variable
   */
  invalidPhoneNumberMessage = () => {
    const { invalidNumberMessageOpen, textFieldValue } = this.state;
    let message = uiString.EMPTY_PHONE_NUMBER;

    if (textFieldValue) {
      const formattedNumber = this.attemptToFormatNumber(textFieldValue);
      message = `${formattedNumber} ${uiString.NOT_A_VALID_PHONE_NUMBER}`;
    }

    return (
      <Confirm
        header={uiString.INVALID_NUMBER}
        content={message}
        open={invalidNumberMessageOpen}
        cancelButton={null}
        onConfirm={this.closeInvalidPhoneNumberMessage}
      />
    );
  };

  /**
   * Try to format the phone number as typed in
   *
   * @param phoneNumber a string of the phone number to format
   * @returns a formatted phone number
   */
  attemptToFormatNumber = (phoneNumber: string) => addLeadingCountryCode(formatPhoneNumber(phoneNumber));

  addPhoneNumberToDial = (phoneNumber: string) => {
    this.setState({ textFieldValue: trimPhoneNumberPlusSign(phoneNumber) });
    this.closeDialogHandler();
  };

  openDialogHandler = () => this.setState({ isOpen: true });

  closeDialogHandler = () => this.setState({ isOpen: false });

  /**
   * Displays Add Contact Icon in the input field
   */
  displayAddContactButton = () => (
    <IconButton
      id={domIds.ADD_CONTACT_PHONE_BUTTON}
      onClick={() => this.openDialogHandler()}
      style={{ background: "none", boxShadow: "none", padding: 0, marginLeft: 4  }}
      disableRipple
      disableTouchRipple
    >
      <AddContactIcon style={{width: 22, height: 25}}/>
    </IconButton>
  );

  /**
   * Removes the text of phone number input field
   */
   handleRemoveButton = () => {
    this.setState({ textFieldValue: "" });
    if (!this.props.lastCalledNumber) {
      this.inputField.focus();
    }
  };

  /**
   * Displays Remove Icon in the input field
   */
  displayRemoveButton = () => {
    const { textFieldValue } = this.state;
    if (textFieldValue.length === 0) return null;

    return (
      <IconButton
        id={domIds.REMOVE_NUMBER_TO_CALL_BUTTON}
        onClick={() => this.handleRemoveButton()}
        style={{ background: "none", boxShadow: "none", padding: 0, marginLeft: 4  }}
        disableRipple
        disableTouchRipple
      >
        <CrossIcon style={{width: 19, height: 22}}/>
      </IconButton>
    );
  };

  /**
   * Processes Key Down event
   */
  onKeyDown = (keyCode) => {
    let keyDownNumber = -1;

    if (keyCode >= keyConstants.CODE_0 && keyCode <= keyConstants.CODE_9) {
      keyDownNumber = keyCode - keyConstants.CODE_0;
    }

    if (
      keyCode >= keyConstants.CODE_NUM_0 &&
      keyCode <= keyConstants.CODE_NUM_9
    ) {
      keyDownNumber = keyCode - keyConstants.CODE_NUM_0;
    }

    this.setState({ keyDownNumber });
  };

  /**
   * Processes Key Up event
   */
  onKeyUp = (keyCode) => {
    this.setState({ keyDownNumber: -1 });

    if (keyCode === keyConstants.CODE_ENTER && !this.props.micCheckInProgress) {
      // Enter code
      this.onCallButtonClick();
    }
  };

  onGlobalKeydown = (event: KeyboardEvent) => {
    const { isPreferencesVisible } = this.props;
    if (/^[ 0-9]/.test(event.key) && this.inputField && !isPreferencesVisible && !this.state.isOpen) {
      this.inputField.focus();
    }
  };

  constructor(props) {
    super(props);
    this.state = {
      textFieldValue:  ``,
      invalidNumberMessageOpen: false,
      keyDownNumber: -1,
      hideLastCalledNumber: true,
      isOpen: false,
    };
  }

  componentDidMount() {
    window.addEventListener("keydown", this.onGlobalKeydown);
    const query = queryString.parse(this.props.search);
    if (query.phoneNumber) {
      this.onTextFieldChanged(query.phoneNumber);
    }
    if (this.props.numberToCall) {
      this.onTextFieldChanged(this.props.numberToCall);
    }
    
    if (!this.props.lastCalledNumber) this.inputField.focus();
  }

  componentDidUpdate(prevProps) {
    const query = queryString.parse(this.props.search);
    const prevQuery = queryString.parse(prevProps.search);
    if (!!query.phoneNumber && query.phoneNumber !== prevQuery.phoneNumber) {
      this.onTextFieldChanged(query.phoneNumber);
      this.props.push(this.props.match.path);
    }
   
    // We need to check if the the numberToCall has been updated (in this case from ViewContact.tsx)
    if(this.props.numberToCall !== prevProps.numberToCall) {
      this.onTextFieldChanged(this.props.numberToCall);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.onGlobalKeydown);
    this.props.setNumberToCall(null);
    
  }

  render() {
    const {
      outgoingPhoneNumber,
      defaultOutgoingPhoneNumber,
      lastCalledNumber,
      micCheckInProgress
    } = this.props;
    const { keyDownNumber, textFieldValue, hideLastCalledNumber } = this.state;
    const formattedNumber = this.attemptToFormatNumber(textFieldValue);
    const formattedLastCalledNumber = this.attemptToFormatNumber(
      lastCalledNumber
    );
    const showRedialButton =
      hideLastCalledNumber && !!lastCalledNumber && !textFieldValue.length;

    return (
      <Grid
        textAlign="center"
        verticalAlign="middle"
        style={{
          height: "100%",
          width: "100%",
          margin: "0px",
          backgroundColor: "var(--color-background-grey)",
        }}
      >
        <Grid.Column style={{ padding: "0px" }}>
          <div>
            <div>
              {this.invalidPhoneNumberMessage()}
              <div
                style={{
                  display: "inline-flex",
                  width: "300px",
                }}
              >
                <input
                  id={domIds.NUMBER_TO_CALL_INPUT}
                  value={formattedNumber}
                  className="text-large"
                  style={{
                    fontSize: "35px",
                    textAlign: "center",
                    border: "none",
                    width: "300px",
                    outline: "none",
                    backgroundColor: "var(--color-background-grey)",
                  }}
                  onChange={(e) => this.onTextFieldChanged(e.target.value)}
                  onKeyDown={(e) => this.onKeyDown(e.keyCode)}
                  onKeyUp={(e) => this.onKeyUp(e.keyCode)}
                  ref={(input) => {
                    this.inputField = input;
                  }}
                  onFocus={() => {
                    this.setState({
                      hideLastCalledNumber: true,
                    });
                  }}
                  placeholder={
                    !this.state.hideLastCalledNumber
                      ? formattedLastCalledNumber
                      : ""
                  }
                />
                {this.displayAddContactButton()}
                {this.displayRemoveButton()}
              </div>
            </div>
            <div>
              <Divider
                style={{
                  width: "440px",
                  display: "inline-flex",
                  borderTop: "1px solid var(--color-dark-grey)",
                  paddingBottom: "1em",
                }}
              />
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div className="keypad_container">
              {/* Row 1 */}
              <KeyComponent
                keyTitle="1"
                clickActionCallback={() => this.onNumPadClick(1)}
                isPressed={keyDownNumber === 1}
              />
              <KeyComponent
                keyTitle="2"
                keySubtitle="ABC"
                clickActionCallback={() => this.onNumPadClick(2)}
                isPressed={keyDownNumber === 2}
              />
              <KeyComponent
                keyTitle="3"
                keySubtitle="DEF"
                clickActionCallback={() => this.onNumPadClick(3)}
                isPressed={keyDownNumber === 3}
              />

              {/* Row 2 */}

              <KeyComponent
                keyTitle="4"
                keySubtitle="GHI"
                clickActionCallback={() => this.onNumPadClick(4)}
                isPressed={keyDownNumber === 4}
              />
              <KeyComponent
                keyTitle="5"
                keySubtitle="JKL"
                clickActionCallback={() => this.onNumPadClick(5)}
                isPressed={keyDownNumber === 5}
              />
              <KeyComponent
                keyTitle="6"
                keySubtitle="MNO"
                clickActionCallback={() => this.onNumPadClick(6)}
                isPressed={keyDownNumber === 6}
              />

              {/* Row 3 */}

              <KeyComponent
                keyTitle="7"
                keySubtitle="PQRS"
                clickActionCallback={() => this.onNumPadClick(7)}
                isPressed={keyDownNumber === 7}
              />
              <KeyComponent
                keyTitle="8"
                keySubtitle="TUV"
                clickActionCallback={() => this.onNumPadClick(8)}
                isPressed={keyDownNumber === 8}
              />
              <KeyComponent
                keyTitle="9"
                keySubtitle="WXYZ"
                clickActionCallback={() => this.onNumPadClick(9)}
                isPressed={keyDownNumber === 9}
              />

              {/* Row 4 */}

              <KeyComponent keyTitle="*" />
              <KeyComponent
                keyTitle="0"
                keySubtitle="+"
                clickActionCallback={() => this.onNumPadClick(0)}
                isPressed={keyDownNumber === 0}
              />
              <KeyComponent keyTitle="#" />

              {/* Row 5 */}
              <KeyComponent type={Types.blank} />
              {showRedialButton ? (
                <KeyComponent
                  id={domIds.IDLE_KEYPAD_REDIAL_BUTTON}
                  type={Types.redial}
                  clickActionCallback={() => this.onCallButtonClick()}
                  isEnabled={!!defaultOutgoingPhoneNumber && !micCheckInProgress}
                />
              ) : (
                <KeyComponent
                  id={domIds.IDLE_KEYPAD_CALL_BUTTON}
                  type={Types.call}
                  clickActionCallback={() => this.onCallButtonClick()}
                  isEnabled={!!defaultOutgoingPhoneNumber && !micCheckInProgress}
                />
              )}
              <KeyComponent type={Types.blank} />
            </div>
          </div>
        </Grid.Column>
        <ContactDialogWithDropdown
          open={this.state.isOpen}
          onClose={this.closeDialogHandler}
          onClick={this.addPhoneNumberToDial}
        />
      </Grid>
    );
  }
}

/**
 * Map props to a redux state
 * @param {*} state
 */
const mapStateToProps = (state) => ({
  defaultOutgoingPhoneNumber: state.callReducer.defaultOutgoingPhoneNumber,
  outgoingPhoneNumber: state.callReducer.outgoingPhoneNumber,
  lastCallOutgoingPhoneNumber: state.callReducer.lastCallOutgoingPhoneNumber,
  lastCalledNumber: state.callReducer.lastCalledNumber,
  numberToCall: state.callReducer.numberToCall,
  search: state.router.location.search,
  defaultAudioInput: state.preferences.defaultAudioInput,
  micIsBlocked: state.micStatus.micIsBlocked,
  micCheckInProgress: state.micStatus.micCheckInProgress,
  isPreferencesVisible: state.preferences.isPreferencesVisible
});

export default withRouter(
  connect(mapStateToProps, {
    makeCall,
    setNumberToCall,
    setLastCalledNumber,
    push,
    openMicUnavailableModal
  })(IdleKeypad as any)
);
